import React, { createContext, useContext, useState, useEffect, useRef } from "react";

interface ProfileScreeningContextValue {
  isRateLimited: boolean;
  retryAfter: number | undefined;
  countdownSeconds: number | undefined;
  limitType: 'hourly' | 'daily' | undefined;
  rateLimitModalOpen: boolean;
  setRateLimitState: (isLimited: boolean, retryAfter?: number, limitType?: 'hourly' | 'daily') => void;
  openRateLimitModal: () => void;
  closeRateLimitModal: () => void;
  /** Session ID from the most recent completed screening result (null if no result yet) */
  screeningSessionId: string | null;
  setScreeningSessionId: (id: string | null) => void;
  /** Job description submitted for the most recent screening (empty string if none) */
  submittedJobDescription: string;
  setSubmittedJobDescription: (jd: string) => void;
}

const ProfileScreeningContext = createContext<ProfileScreeningContextValue | undefined>(undefined);

export const ProfileScreeningProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [retryAfter, setRetryAfter] = useState<number | undefined>(undefined);
  const [countdownSeconds, setCountdownSeconds] = useState<number | undefined>(undefined);
  const [limitType, setLimitType] = useState<'hourly' | 'daily' | undefined>(undefined);
  const [rateLimitModalOpen, setRateLimitModalOpen] = useState(false);
  const [screeningSessionId, setScreeningSessionId] = useState<string | null>(null);
  const [submittedJobDescription, setSubmittedJobDescription] = useState("");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearCountdown = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const setRateLimitState = (isLimited: boolean, after?: number, lt?: 'hourly' | 'daily') => {
    if (isLimited) {
      setIsRateLimited(true);
      setRetryAfter(after);
      setLimitType(lt);
      setCountdownSeconds(after);

      // Persist to sessionStorage for page refresh recovery
      const expiresAt = Date.now() + (after ?? 0) * 1000;
      sessionStorage.setItem(
        'screening_rate_limit',
        JSON.stringify({ expiresAt, limitType: lt })
      );

      // Clear any existing interval
      clearCountdown();

      // Start new countdown interval
      intervalRef.current = setInterval(() => {
        const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
        if (remaining > 0) {
          setCountdownSeconds(remaining);
        } else {
          setCountdownSeconds(0);
          clearCountdown();
          // Auto-clear rate limit when countdown reaches 0
          setRateLimitState(false);
        }
      }, 1000);
    } else {
      setIsRateLimited(false);
      setRetryAfter(undefined);
      setCountdownSeconds(undefined);
      setLimitType(undefined);
      clearCountdown();
      sessionStorage.removeItem('screening_rate_limit');
    }
  };

  // Restore from sessionStorage on mount
  useEffect(() => {
    const raw = sessionStorage.getItem('screening_rate_limit');
    if (!raw) return;
    try {
      const { expiresAt, limitType: lt } = JSON.parse(raw);
      const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
      if (remaining > 0) {
        setRateLimitState(true, remaining, lt);
      } else {
        sessionStorage.removeItem('screening_rate_limit');
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []);

  const openRateLimitModal = () => setRateLimitModalOpen(true);
  const closeRateLimitModal = () => setRateLimitModalOpen(false);

  return (
    <ProfileScreeningContext.Provider
      value={{
        isRateLimited,
        retryAfter,
        countdownSeconds,
        limitType,
        rateLimitModalOpen,
        setRateLimitState,
        openRateLimitModal,
        closeRateLimitModal,
        screeningSessionId,
        setScreeningSessionId,
        submittedJobDescription,
        setSubmittedJobDescription,
      }}
    >
      {children}
    </ProfileScreeningContext.Provider>
  );
};

export const useProfileScreening = (): ProfileScreeningContextValue => {
  const ctx = useContext(ProfileScreeningContext);
  if (!ctx) {
    throw new Error("useProfileScreening must be used within a ProfileScreeningProvider");
  }
  return ctx;
};
